/**
 * CSS Custom Properties (Design Tokens)
 *
 * This file contains all CSS variables that define the grid's visual appearance.
 * Override these properties to customize the grid's look and feel.
 *
 * @layer tbw-base
 */

@layer tbw-base {
  tbw-grid {
    /* ========== Base Design Tokens ========== */
    /* Override these for bulk changes. Uses em units so everything
       scales proportionally with font size. */

    --tbw-base-icon-size: 1em; /* Icons scale with font */
    --tbw-base-radius: 0.25em; /* Border radius */

    /* ========== Typography ========== */
    /* All em-based, relative to inherited font size */
    --tbw-font-size: 1em;
    --tbw-font-size-sm: 0.9285em;
    --tbw-font-size-xs: 0.7857em;
    --tbw-font-size-2xs: 0.7142em;

    /* ========== Spacing Scale ========== */
    /* Em-based spacing scales with font size */
    --tbw-spacing-xs: 0.25em; /* ~4px at 16px font */
    --tbw-spacing-sm: 0.375em; /* ~6px */
    --tbw-spacing-md: 0.5em; /* ~8px */
    --tbw-spacing-lg: 0.75em; /* ~12px */
    --tbw-spacing-xl: 1em; /* ~16px */

    /* ========== Icon Sizes ========== */
    --tbw-icon-size: var(--tbw-base-icon-size);
    --tbw-icon-size-sm: 0.875em;
    --tbw-checkbox-size: var(--tbw-base-icon-size);
    --tbw-toggle-size: 1.25em;

    /* ========== Border Radius ========== */
    --tbw-border-radius: var(--tbw-base-radius);

    /* ========== Core Palette ========== */
    /* Colors use light-dark() for automatic theme support */
    --tbw-color-bg: transparent;
    --tbw-color-panel-bg: light-dark(#eeeeee, #222222);
    --tbw-color-fg: light-dark(#222222, #eeeeee);
    --tbw-color-fg-muted: light-dark(#555555, #aaaaaa);
    --tbw-color-accent: light-dark(#3b82f6, #3b82f6);
    --tbw-color-accent-fg: light-dark(#ffffff, #000000);
    /* `--tbw-color-accent` is a *surface* token (focus rings, borders, fills) and
       is deliberately vivid — at ~3:1 on white it is not safe as a text color.
       Use `--tbw-color-accent-text` when accent is applied to text (WCAG 1.4.3). */
    --tbw-color-accent-text: light-dark(#1d4ed8, #93c5fd);
    /* Status colors double as text colors (e.g. context-menu danger items, cell
       renderers), so both stops must clear WCAG 1.4.3 AA (4.5:1) against the
       light and dark surfaces. success 5.33:1 on #fff, error 4.95:1 on #222. */
    --tbw-color-success: light-dark(hsl(122, 39%, 34%), hsl(122, 39%, 49%));
    --tbw-color-warning: light-dark(hsl(38, 92%, 50%), hsl(38, 92%, 50%));
    --tbw-color-error: light-dark(hsl(0, 65%, 51%), hsl(0, 65%, 65%));
    --tbw-color-danger: var(--tbw-color-error);
    --tbw-color-selection: light-dark(#fff7d6, #333333);
    --tbw-color-row-alt: var(--tbw-color-bg);
    --tbw-color-row-hover: light-dark(#f0f6ff, #1c1c1c);
    --tbw-color-header-bg: color-mix(in hsl, var(--tbw-color-panel-bg) 85%, var(--tbw-color-fg));
    --tbw-color-header-fg: color-mix(in hsl, var(--tbw-color-fg) 75%, var(--tbw-color-panel-bg));
    --tbw-color-border: light-dark(#d0d0d4, #454545);
    --tbw-color-border-strong: light-dark(#777777, #666666);
    --tbw-color-border-cell: var(--tbw-color-border);
    --tbw-color-border-header: var(--tbw-color-border);
    --tbw-color-shadow: light-dark(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.3));

    /* ========== Typography (derived from base) ========== */
    --tbw-font-family: inherit;
    --tbw-font-size-header: var(--tbw-font-size);
    --tbw-font-weight-header: bold;

    /* ========== Cell & Row Dimensions ========== */
    --tbw-cell-padding-header: var(--tbw-spacing-xs) var(--tbw-spacing-md);
    --tbw-cell-padding-v: var(--tbw-spacing-xs);
    --tbw-cell-padding-h: var(--tbw-spacing-md);
    --tbw-cell-padding: var(--tbw-cell-padding-v) var(--tbw-cell-padding-h);
    --tbw-cell-padding-input: var(--tbw-spacing-xs) var(--tbw-spacing-sm);
    --tbw-row-height: 1.75em; /* ~28px at 16px font */
    --tbw-header-height: 1.875em; /* ~30px at 16px font */
    --tbw-cell-white-space: nowrap;

    /* ========== Borders ========== */
    --tbw-border-width: 1px;
    --tbw-border-style: solid;
    --tbw-border-input: var(--tbw-border-width) var(--tbw-border-style) var(--tbw-color-border-strong);
    --tbw-border-header: var(--tbw-border-width) var(--tbw-border-style) var(--tbw-color-border-header);
    --tbw-row-divider: var(--tbw-border-width) var(--tbw-border-style) var(--tbw-color-border-cell);
    --tbw-row-hover-outline: 0;
    --tbw-color-active-row-bg: var(--tbw-color-selection);
    --tbw-active-row-outline: 0;

    /* ========== Focus & Outline ========== */
    --tbw-focus-outline-width: 2px;
    --tbw-focus-outline: var(--tbw-focus-outline-width) var(--tbw-border-style) var(--tbw-color-accent);
    --tbw-focus-outline-offset: -2px;
    --tbw-focus-background: rgba(from var(--tbw-color-accent) r g b / 12%);

    /* Range/row selection */
    --tbw-range-border-color: var(--tbw-color-accent);
    --tbw-range-selection-bg: rgba(from var(--tbw-range-border-color) r g b / 12%);

    /* Column selection (selection plugin, column axis) */
    --tbw-column-selection-bg: rgba(from var(--tbw-color-accent) r g b / 12%);

    /* ========== Resize Handle ========== */
    --tbw-resize-handle-width: 2px;
    --tbw-resize-handle-color: transparent;
    --tbw-resize-handle-color-hover: var(--tbw-color-accent);
    --tbw-resize-handle-border-radius: 0;
    --tbw-resize-indicator-width: 2px;
    --tbw-resize-indicator-color: var(--tbw-color-accent);
    --tbw-resize-indicator-opacity: 0.6;

    /* ========== Animation / Transitions ========== */
    --tbw-transition-duration: 120ms;
    --tbw-transition-ease: ease;
    --tbw-animation-duration: 200ms;
    --tbw-animation-easing: ease-out;
    --tbw-animation-enabled: 1;

    /* Row animations (change/insert/remove) */
    --tbw-row-change-duration: 500ms;
    --tbw-row-insert-duration: 300ms;
    --tbw-row-remove-duration: 200ms;
    --tbw-row-change-color: rgba(from var(--tbw-color-accent) r g b / 25%);

    /* ========== Sorting Indicators ========== */
    --tbw-sort-indicator-color: var(--tbw-color-fg-muted);
    --tbw-sort-indicator-active-color: var(--tbw-color-accent);
    --tbw-sort-indicator-display: inline-flex;
    --tbw-sort-indicator-visibility: visible;

    /* ========== Column Headers ========== */
    --tbw-header-text-transform: none;
    --tbw-header-letter-spacing: normal;
    --tbw-color-header-separator: var(--tbw-color-border-cell);

    /* ========== Touch / Pointer ========== */
    /* Minimum hit-target dimension for interactive controls, on every pointer
       type — SC 2.5.8 is not conditional on touch. Small controls reach it with
       a transparent overlay rather than a bigger box, so density is unchanged.
       24 × 24 px satisfies WCAG 2.2 SC 2.5.8 Target Size (Minimum), Level AA.
       Override to 44px to also satisfy SC 2.5.5 Target Size (Enhanced), Level AAA. */
    --tbw-touch-target-min: 24px;

    /* ========== Density Scaling ========== */
    --tbw-density-scale: 1;

    /* ========== Shell Header ========== */
    --tbw-shell-header-height: 2.75em; /* ~44px at 16px font */
    --tbw-shell-header-bg: var(--tbw-color-panel-bg);
    --tbw-shell-header-border: var(--tbw-color-border);
    --tbw-shell-title-font-size: var(--tbw-font-size);
    --tbw-shell-title-font-weight: 600;

    /* ========== Tool Panel ========== */
    --tbw-tool-panel-width: 17.5em; /* ~280px at 16px font */
    --tbw-tool-panel-bg: var(--tbw-color-panel-bg);
    --tbw-tool-panel-border: var(--tbw-color-border);
    --tbw-tool-panel-header-height: 2.5em; /* ~40px at 16px font */
    --tbw-tool-panel-transition: var(--tbw-animation-duration) var(--tbw-animation-easing);

    /* ========== Toolbar ========== */
    --tbw-toolbar-button-size: 2em; /* ~32px at 16px font */
    --tbw-toolbar-button-gap: var(--tbw-spacing-xs);

    /* ========== Component Shortcuts ========== */
    --tbw-panel-padding: var(--tbw-spacing-lg);
    --tbw-panel-gap: var(--tbw-spacing-md);
    --tbw-menu-item-padding: var(--tbw-spacing-sm) var(--tbw-spacing-lg);
    --tbw-menu-item-gap: var(--tbw-spacing-md);
    --tbw-menu-min-width: 10rem; /* ~160px */
    --tbw-button-padding: var(--tbw-spacing-sm) var(--tbw-spacing-lg);
    --tbw-button-padding-sm: var(--tbw-spacing-xs) var(--tbw-spacing-md);
    --tbw-input-height: var(--tbw-row-height);
    --tbw-input-padding: 0 var(--tbw-spacing-md);
    --tbw-detail-padding: var(--tbw-spacing-xl);
    --tbw-detail-max-height: 31.25rem; /* ~500px */
    --tbw-indicator-size: var(--tbw-spacing-sm);

    /* ========== Icon Content (CSS-based icons) ========== */
    /* Text/emoji icons — used via content: var(...) in ::before pseudo-elements */
    --tbw-icon-expand: '▶';
    --tbw-icon-collapse: '▼';
    --tbw-icon-sort-asc: '▲';
    --tbw-icon-sort-desc: '▼';
    --tbw-icon-sort-none: '⇅';
    --tbw-icon-submenu-arrow: '▶';
    --tbw-icon-drag-handle: '⋮⋮';
    --tbw-icon-tool-panel: '☰';
    --tbw-icon-filter: ''; /* Uses mask strategy (SVG) by default */
    --tbw-icon-filter-active: ''; /* Uses mask strategy (SVG) by default */
    --tbw-icon-print: '🖨️';

    /* SVG mask-based icons — set --tbw-icon-<key>: '' and provide a -mask to switch to SVG */
    --tbw-icon-expand-mask: none;
    --tbw-icon-collapse-mask: none;
    --tbw-icon-sort-asc-mask: none;
    --tbw-icon-sort-desc-mask: none;
    --tbw-icon-sort-none-mask: none;
    --tbw-icon-submenu-arrow-mask: none;
    --tbw-icon-drag-handle-mask: none;
    --tbw-icon-tool-panel-mask: none;
    --tbw-icon-filter-mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' width='12' height='12' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='currentColor' d='M6 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z'/%3E%3C/svg%3E");
    --tbw-icon-filter-active-mask: var(--tbw-icon-filter-mask);
    --tbw-icon-print-mask: none;
  }
}
